import { call, delay, put, race, select, take, takeLatest } from "redux-saga/effects";
import { ddmTheme } from "@ddm-design-system/tokens";
import { IAppContext } from "../services";
import { IJCoreService } from "../services/jcore";
import { getCurrentContent } from "../store/content/selectors";
import {
  applyX10ConfigError,
  applyX10ConfigSuccess,
  getX10ConfigError,
  getX10ConfigHistorySuccess,
  getX10ConfigSuccess,
  pingX10Done
} from "../store/x10config/actions";
import { nextPollDelayMs, PING_FOLLOW_UP } from "../store/x10config/polling";
import { getX10Configuration } from "../store/x10config/selectors";
import {
  APPLY_X10_CONFIG,
  GET_X10_CONFIG,
  GET_X10_CONFIG_HISTORY,
  IApplyX10ConfigAction,
  IGetX10ConfigAction,
  IGetX10ConfigHistoryAction,
  IPingX10Action,
  IStartX10ConfigPollingAction,
  IX10ConfigBlocked,
  IX10ConfigCommand,
  IX10Configuration,
  IX10ConfigPingResult,
  PING_X10,
  START_X10_CONFIG_POLLING,
  STOP_X10_CONFIG_POLLING
} from "../store/x10config/types";

export function* fetchX10Config(jCoreService: IJCoreService, x10Id: string) {
  try {
    const configuration: IX10Configuration = yield call(
      [jCoreService, jCoreService.getX10Configuration],
      x10Id
    );
    yield put(getX10ConfigSuccess(configuration));
  } catch {
    yield put(getX10ConfigError());
  }
}

export function* fetchX10ConfigHistory(jCoreService: IJCoreService, x10Id: string) {
  try {
    const history: IX10ConfigCommand[] = yield call(
      [jCoreService, jCoreService.getX10ConfigurationHistory],
      x10Id
    );
    yield put(getX10ConfigHistorySuccess(x10Id, history));
  } catch {
    // The history is secondary; the next change refetches it.
  }
}

export function* pingX10(jCoreService: IJCoreService, x10Id: string) {
  try {
    const result: IX10ConfigPingResult = yield call([jCoreService, jCoreService.pingX10], x10Id);
    yield put(pingX10Done(result));
    return result;
  } catch {
    yield put(pingX10Done(null));
    return null;
  }
}

/** Pings, then refreshes a few times so the heartbeat that answers the ping shows up quickly. */
function* pingAndFollowUp(jCoreService: IJCoreService, action: IPingX10Action) {
  const result: IX10ConfigPingResult | null = yield call(pingX10, jCoreService, action.payload);
  if (!result?.pinged) {
    return;
  }
  for (let attempt = 0; attempt < PING_FOLLOW_UP.attempts; attempt += 1) {
    yield delay(PING_FOLLOW_UP.intervalMs);
    yield call(fetchX10Config, jCoreService, action.payload);
    const configuration: IX10Configuration | undefined = yield select(
      getX10Configuration(action.payload)
    );
    if (configuration?.online.verified) {
      return;
    }
  }
}

/** Runs while the Configuration tab is open: ping once, then poll at a pace that fits what is pending. */
export function* pollX10Config(jCoreService: IJCoreService, action: IStartX10ConfigPollingAction) {
  const x10Id = action.payload;
  const startedAt = Date.now();
  yield call(pingX10, jCoreService, x10Id);
  yield call(fetchX10ConfigHistory, jCoreService, x10Id);
  while (true) {
    yield call(fetchX10Config, jCoreService, x10Id);
    const configuration: IX10Configuration | undefined = yield select(getX10Configuration(x10Id));
    const wait = nextPollDelayMs(configuration, Date.now() - startedAt, document.hidden);
    const { stopped } = yield race({
      stopped: take(STOP_X10_CONFIG_POLLING),
      tick: delay(wait)
    });
    if (stopped) {
      return;
    }
  }
}

const blockedFrom = (error: any): IX10ConfigBlocked | null => {
  const response = error?.response;
  if (response?.status === 409 && response.data?.reason) {
    return {
      reason: response.data.reason,
      message: response.data.message,
      retryAfterSeconds: response.data.retryAfterSeconds ?? null
    };
  }
  return null;
};

export function* applyX10Config(
  { jCoreService, notificationService }: IAppContext,
  action: IApplyX10ConfigAction
) {
  const { notifications: content = {} } = (yield select(getCurrentContent)) || {};
  const { x10Id, update } = action.payload;
  const errorIcon = { name: "Warning", fill: ddmTheme.colors.alert.alert100 };

  try {
    const command: IX10ConfigCommand = yield call(
      [jCoreService, jCoreService.applyX10Configuration],
      x10Id,
      update
    );
    yield put(applyX10ConfigSuccess(x10Id, command));
    if (command.status === "FAILED") {
      notificationService?.addNotification({
        iconProps: errorIcon,
        text:
          content.x10_config_send_failed ||
          `The change was not sent: ${command.statusDetail || "unknown error"}`
      });
    } else {
      notificationService?.addNotification({
        iconProps: { name: "ok" },
        text: content.x10_config_sent || "Change sent to the unit. Waiting for it to confirm."
      });
    }
  } catch (error) {
    const blocked = blockedFrom(error);
    yield put(applyX10ConfigError(blocked));
    notificationService?.addNotification({
      iconProps: errorIcon,
      text:
        blocked?.message ||
        error?.response?.data?.message ||
        content.x10_config_send_error ||
        "The change could not be sent."
    });
  }
  yield call(fetchX10Config, jCoreService, x10Id);
  yield call(fetchX10ConfigHistory, jCoreService, x10Id);
}

export function* x10ConfigWatcher(context: IAppContext) {
  yield takeLatest(START_X10_CONFIG_POLLING, pollX10Config, context.jCoreService);
  yield takeLatest(GET_X10_CONFIG, (action: IGetX10ConfigAction) =>
    fetchX10Config(context.jCoreService, action.payload)
  );
  yield takeLatest(GET_X10_CONFIG_HISTORY, (action: IGetX10ConfigHistoryAction) =>
    fetchX10ConfigHistory(context.jCoreService, action.payload)
  );
  yield takeLatest(PING_X10, pingAndFollowUp, context.jCoreService);
  yield takeLatest(APPLY_X10_CONFIG, applyX10Config, context);
}

export default x10ConfigWatcher;
